import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Search, Loader2, UserRound, FileDown, FileText, Send, MessageSquare } from "lucide-react";

const empty = { name: "", national_id: "", grade: "", class_name: "", parent_phone: "" };

export default function StudentsManager({ schoolId, schoolName }) {
  const { user } = useAuth();
  const isCounselor = !schoolId && user?.role === "COUNSELOR";
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState({ grades: [], classes: [] });
  const [grade, setGrade] = useState("all");
  const [className, setClassName] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, pages_done: 0, total_pages: 0 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRows, setReviewRows] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickStudent, setQuickStudent] = useState(null);
  const [quickTemplate, setQuickTemplate] = useState("absence");
  const [quickCustom, setQuickCustom] = useState("");
  const [quickSending, setQuickSending] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (schoolId) p.school_id = schoolId;
    if (grade !== "all") p.grade = grade;
    if (className !== "all") p.class_name = className;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [schoolId, grade, className, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get("/students", { params: params() }),
        api.get("/students/meta", { params: schoolId ? { school_id: schoolId } : {} }),
      ]);
      setStudents(s.data);
      setMeta(m.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [params, schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isCounselor) api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [isCounselor]);

  const openQuick = (s) => { setQuickStudent(s); setQuickTemplate("absence"); setQuickCustom(""); setQuickOpen(true); };
  const doQuickSend = async () => {
    if (quickTemplate === "custom" && !quickCustom.trim()) { toast.error("يرجى كتابة نص الرسالة"); return; }
    setQuickSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        student_ids: [quickStudent.id],
        template_id: quickTemplate,
        custom_body: quickTemplate === "custom" ? quickCustom : null,
      });
      if (data.queued > 0) toast.success(`تم إرسال الرسالة إلى ولي أمر ${quickStudent.name}`);
      else if (data.skipped > 0) toast.error("لا يوجد رقم جوال مسجل لهذا الطالب");
      else toast.error("تعذر الإرسال");
      setQuickOpen(false);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setQuickSending(false);
    }
  };

  const openAdd = () => { setForm(empty); setEditId(null); setDialogOpen(true); };
  const openEdit = (st) => { setForm(st); setEditId(st.id); setDialogOpen(true); };

  const save = async () => {
    if (!form.name || !form.grade || !form.class_name) {
      toast.error("يرجى تعبئة الاسم والصف والفصل");
      return;
    }
    try {
      const payload = { ...form };
      if (schoolId) payload.school_id = schoolId;
      if (editId) await api.put(`/students/${editId}`, payload);
      else await api.post("/students", payload);
      toast.success(editId ? "تم تعديل بيانات الطالب" : "تمت إضافة الطالب");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/students/${id}`);
      toast.success("تم حذف الطالب");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const exportPDF = () => {
    if (!students.length) { toast.error("لا يوجد طلاب للتصدير"); return; }
    const grouped = {};
    students.forEach((s) => {
      const g = s.grade || "—"; const c = s.class_name || "—";
      grouped[g] = grouped[g] || {};
      grouped[g][c] = grouped[g][c] || [];
      grouped[g][c].push(s);
    });
    const today = new Date().toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { year: "numeric", month: "long", day: "numeric" });
    let sections = "";
    Object.keys(grouped).sort().forEach((g) => {
      Object.keys(grouped[g]).sort().forEach((c) => {
        const rows = grouped[g][c].map((s, i) =>
          `<tr><td>${i + 1}</td><td>${s.name}</td><td>${s.parent_phone || '<span class="miss">غير مسجل</span>'}</td></tr>`).join("");
        sections += `<h2>${g} — الفصل ${c} <span class="cnt">(${grouped[g][c].length} طالب)</span></h2>
          <table><thead><tr><th>#</th><th>اسم الطالب</th><th>رقم الجوال</th></tr></thead><tbody>${rows}</tbody></table>`;
      });
    });
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>قائمة الطلاب</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">
      <style>*{font-family:'Cairo',sans-serif;box-sizing:border-box;}body{padding:24px;color:#0f172a;}
      .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #065f46;padding-bottom:12px;margin-bottom:8px;}
      .head h1{color:#065f46;margin:0;font-size:22px;}.head .meta{color:#64748b;font-size:12px;text-align:left;}
      h2{background:#ecfdf5;color:#065f46;padding:8px 12px;border-radius:8px;font-size:15px;margin:18px 0 8px;}
      .cnt{color:#059669;font-weight:400;font-size:12px;}
      table{width:100%;border-collapse:collapse;margin-bottom:8px;}
      th,td{border:1px solid #e2e8f0;padding:7px 10px;text-align:right;font-size:13px;}
      th{background:#f1f5f9;color:#334155;}.miss{color:#dc2626;font-weight:600;}
      @media print{.noprint{display:none;}}</style></head><body>
      <div class="head"><h1>${schoolName || "قائمة الطلاب"}</h1>
      <div class="meta">قائمة الطلاب<br>${today}<br>الإجمالي: ${students.length} طالب</div></div>
      ${sections || "<p>لا يوجد طلاب.</p>"}
      <button class="noprint" onclick="window.print()" style="margin-top:16px;padding:10px 20px;background:#065f46;color:#fff;border:none;border-radius:8px;cursor:pointer;font-family:'Cairo';">طباعة / حفظ PDF</button>
      <script>setTimeout(function(){window.print();},700);</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("يرجى السماح بالنوافذ المنبثقة لتصدير PDF"); return; }
    w.document.write(html);
    w.document.close();
  };

  const exportCSV = () => {
    if (!students.length) { toast.error("لا يوجد طلاب للتصدير"); return; }
    const header = ["اسم الطالب", "رقم الهوية", "الصف", "الفصل", "رقم الجوال"];
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const rows = students.map((s) => [s.name, s.national_id, s.grade, s.class_name, s.parent_phone].map(esc).join(","));
    const csv = "\uFEFF" + [header.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students_${(schoolName || "list").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!file) { toast.error("يرجى اختيار ملف"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (schoolId) fd.append("school_id", schoolId);
      const { data } = await api.post("/students/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`تم استيراد ${data.imported} طالب${data.skipped ? ` (تم تجاهل ${data.skipped} صف)` : ""}`);
      setImportOpen(false);
      setFile(null);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setImporting(false);
    }
  };

  const doParsePDF = async () => {
    if (!pdfFile) { toast.error("يرجى اختيار ملف PDF"); return; }
    setParsing(true);
    setProgress({ percent: 0, pages_done: 0, total_pages: 0 });
    try {
      const fd = new FormData();
      fd.append("file", pdfFile);
      if (schoolId) fd.append("school_id", schoolId);
      const { data } = await api.post("/students/parse-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = data.job_id;
      const started = Date.now();
      let result = null;
      while (Date.now() - started < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/students/parse-pdf/${jobId}`);
        setProgress({ percent: st.percent || 0, pages_done: st.pages_done || 0, total_pages: st.total_pages || 0 });
        if (st.status === "done") { result = st; break; }
        if (st.status === "error") { throw new Error(st.error || "فشل تحليل الملف"); }
      }
      if (!result) throw new Error("انتهت مهلة التحليل، حاول بملف أصغر");
      if (!result.count) { toast.error("لم يتم العثور على طلاب في الملف"); return; }
      setReviewRows(result.students.map((s) => ({ ...s })));
      setReviewSummary(result.summary || null);
      setPdfOpen(false);
      setPdfFile(null);
      setReviewOpen(true);
    } catch (e) {
      toast.error(e.response ? formatError(e) : (e.message || "فشل تحليل الملف"));
    } finally {
      setParsing(false);
    }
  };

  const updateReviewRow = (i, key, v) => {
    setReviewRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  };
  const removeReviewRow = (i) => {
    setReviewRows((rows) => rows.filter((_, idx) => idx !== i));
  };

  const confirmImport = async () => {
    const valid = reviewRows.filter((r) => (r.name || "").trim());
    if (!valid.length) { toast.error("لا يوجد طلاب للاستيراد"); return; }
    setSavingReview(true);
    try {
      const payload = { students: valid };
      if (schoolId) payload.school_id = schoolId;
      const { data } = await api.post("/students/bulk", payload);
      const dupMsg = data.skipped_duplicates ? `، تم تجاهل ${data.skipped_duplicates} مكرر` : "";
      toast.success(`تم استيراد البيانات بنجاح (${data.imported} طالب${dupMsg})`);
      setReviewOpen(false);
      setReviewRows([]);
      setReviewSummary(null);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSavingReview(false);
    }
  };

  const deleteAll = async () => {
    setDeletingAll(true);
    try {
      const { data } = await api.delete("/students/all", { params: schoolId ? { school_id: schoolId } : {} });
      toast.success(`تم حذف ${data.deleted} طالب`);
      setDeleteAllOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[150px] flex-1">
          <Label className="mb-1 block text-xs text-slate-500">الصف</Label>
          <Select value={grade} onValueChange={(v) => { setGrade(v); setClassName("all"); }}>
            <SelectTrigger data-testid="filter-grade"><SelectValue placeholder="كل الصفوف" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الصفوف</SelectItem>
              {meta.grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px] flex-1">
          <Label className="mb-1 block text-xs text-slate-500">الفصل</Label>
          <Select value={className} onValueChange={setClassName}>
            <SelectTrigger data-testid="filter-class"><SelectValue placeholder="كل الفصول" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الفصول</SelectItem>
              {meta.classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px] flex-1">
          <Label className="mb-1 block text-xs text-slate-500">بحث بالاسم</Label>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="اسم الطالب"
              className="pr-9" data-testid="search-student" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportPDF} className="gap-2" data-testid="export-pdf-button">
            <FileDown className="h-4 w-4" /> تصدير PDF
          </Button>
          <Button variant="outline" onClick={exportCSV} className="gap-2" data-testid="export-csv-button">
            <FileDown className="h-4 w-4" /> تصدير Excel
          </Button>
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-students-button">
                <Upload className="h-4 w-4" /> استيراد
              </Button>
            </DialogTrigger>
            <DialogContent className="text-right">
              <DialogHeader><DialogTitle>استيراد الطلاب من Excel / CSV</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">
                يجب أن يحتوي الملف على الأعمدة: اسم الطالب، رقم الهوية (اختياري)، الصف، الفصل، رقم الجوال.
              </p>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])}
                data-testid="import-file-input" />
              <DialogFooter>
                <Button onClick={doImport} disabled={importing} data-testid="confirm-import-button"
                  className="bg-emerald-800 hover:bg-emerald-900">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "رفع واستيراد"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-pdf-button">
                <FileText className="h-4 w-4" /> استيراد PDF
              </Button>
            </DialogTrigger>
            <DialogContent className="text-right">
              <DialogHeader><DialogTitle>استيراد الطلاب من ملف PDF</DialogTitle></DialogHeader>
              <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 p-3 text-xs leading-relaxed text-slate-600" data-testid="noor-guide">
                <p className="mb-1 font-bold text-emerald-800">طريقة استخراج الملف المطلوب من نظام نور:</p>
                <ol className="mr-4 list-decimal space-y-0.5">
                  <li>سجّل الدخول بحساب <b>مدير المدرسة</b> أو موظف لديه صلاحية الدخول إلى التقارير.</li>
                  <li>افتح: <b>التقارير</b>.</li>
                  <li>ثم: <b>تقارير الطلاب</b>.</li>
                  <li>ثم: <b>كشف الطلاب</b>.</li>
                  <li>حمّل الملف بصيغة <b>PDF</b>، ثم ارفعه هنا لاستيراده.</li>
                </ol>
              </div>
              <p className="text-sm text-slate-500">
                سيقرأ النظام الصف والفصل من ترويسة كل صفحة ويستخرج الأسماء وأرقام الجوال، ثم يعرض لك معاينة وملخصاً قبل الاعتماد والحفظ.
                <br />قد يستغرق تحليل الملفات الكبيرة دقيقة أو أكثر، يرجى الانتظار دون إغلاق النافذة.
              </p>
              <Input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])}
                data-testid="pdf-file-input" />
              {parsing && (
                <div className="space-y-2" data-testid="pdf-progress">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>جارٍ تحليل الملف...</span>
                    <span className="font-semibold text-emerald-700" data-testid="pdf-progress-percent">{progress.percent}%</span>
                  </div>
                  <Progress value={progress.percent} className="[&>div]:bg-emerald-700" />
                  {progress.total_pages > 0 && (
                    <p className="text-xs text-slate-500" data-testid="pdf-progress-pages">
                      تم تحليل {progress.pages_done} من {progress.total_pages} صفحة
                    </p>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button onClick={doParsePDF} disabled={parsing} data-testid="analyze-pdf-button"
                  className="bg-emerald-800 hover:bg-emerald-900">
                  {parsing ? <><Loader2 className="ml-2 h-4 w-4 animate-spin" /> جارٍ التحليل...</> : "تحليل الملف"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={openAdd} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="add-student-button">
            <Plus className="h-4 w-4" /> إضافة طالب
          </Button>
          <Button variant="outline" onClick={() => setDeleteAllOpen(true)} disabled={!students.length}
            className="gap-2 border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-all-students-button">
            <Trash2 className="h-4 w-4" /> حذف جميع الطلاب
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <UserRound className="h-10 w-10" />
              <p>لا يوجد طلاب مطابقون</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">اسم الطالب</TableHead>
                  <TableHead className="text-right">الصف</TableHead>
                  <TableHead className="text-right">الفصل</TableHead>
                  <TableHead className="text-right">رقم الجوال</TableHead>
                  <TableHead className="text-center">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell className="font-semibold text-slate-800">{s.name}</TableCell>
                    <TableCell>{s.grade}</TableCell>
                    <TableCell>{s.class_name}</TableCell>
                    <TableCell dir="ltr" className="text-right font-mono text-sm">{s.parent_phone || <span className="text-red-500">غير مسجل</span>}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        {isCounselor && (
                          <Button variant="ghost" size="icon" onClick={() => openQuick(s)}
                            disabled={!s.parent_phone} title={s.parent_phone ? "إرسال سريع" : "لا يوجد رقم جوال"}
                            data-testid={`quick-send-${s.id}`}>
                            <Send className={`h-4 w-4 ${s.parent_phone ? "text-emerald-700" : "text-slate-300"}`} />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)}
                          data-testid={`edit-student-${s.id}`}>
                          <Pencil className="h-4 w-4 text-emerald-700" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-student-${s.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="text-right">
                            <AlertDialogHeader>
                              <AlertDialogTitle>حذف الطالب</AlertDialogTitle>
                              <AlertDialogDescription>هل تريد حذف الطالب {s.name}؟ لا يمكن التراجع.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(s.id)} className="bg-red-600 hover:bg-red-700">
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <p className="text-sm text-slate-400">العدد: {students.length} طالب</p>

      {/* Preview & summary before import */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="text-right sm:max-w-5xl">
          <DialogHeader><DialogTitle>معاينة نتيجة قراءة الملف</DialogTitle></DialogHeader>
          {reviewSummary && (
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-emerald-50 p-3 text-sm sm:grid-cols-3" data-testid="review-summary">
              <div>إجمالي الطلاب: <b data-testid="summary-total">{reviewSummary.total}</b></div>
              <div>لديهم رقم جوال: <b className="text-emerald-700">{reviewSummary.with_phone}</b></div>
              <div>بدون رقم جوال: <b className="text-red-600">{reviewSummary.without_phone}</b></div>
              <div>عدد الصفوف: <b>{reviewSummary.grades_count}</b></div>
              <div>عدد الفصول: <b>{reviewSummary.classes_count}</b></div>
              <div>مكررون تم استبعادهم: <b>{reviewSummary.duplicates_removed}</b></div>
            </div>
          )}
          <p className="text-xs text-slate-500">راجع البيانات قبل الاعتماد. يمكنك تعديل أي خانة أو حذف صف. الطالب بدون رقم جوال سيُستورد والخانة فارغة.</p>
          <div className="max-h-[50vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50">
                <TableRow>
                  <TableHead className="text-right">اسم الطالب</TableHead>
                  <TableHead className="text-right">الصف</TableHead>
                  <TableHead className="text-right">الفصل</TableHead>
                  <TableHead className="text-right">رقم الجوال</TableHead>
                  <TableHead className="text-center">حذف</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewRows.map((r, i) => (
                  <TableRow key={i} data-testid={`review-row-${i}`}>
                    <TableCell><Input value={r.name || ""} onChange={(e) => updateReviewRow(i, "name", e.target.value)} className="min-w-[150px]" data-testid={`review-name-${i}`} /></TableCell>
                    <TableCell><Input value={r.grade || ""} onChange={(e) => updateReviewRow(i, "grade", e.target.value)} className="min-w-[110px]" /></TableCell>
                    <TableCell><Input value={r.class_name || ""} onChange={(e) => updateReviewRow(i, "class_name", e.target.value)} className="min-w-[80px]" /></TableCell>
                    <TableCell><Input value={r.parent_phone || ""} onChange={(e) => updateReviewRow(i, "parent_phone", e.target.value)} dir="ltr" placeholder="غير مسجل" className="min-w-[120px] text-right font-mono" data-testid={`review-phone-${i}`} /></TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="icon" onClick={() => removeReviewRow(i)} data-testid={`review-delete-${i}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setReviewOpen(false); setReviewRows([]); }} data-testid="cancel-review-button">إلغاء</Button>
            <Button onClick={confirmImport} disabled={savingReview} className="bg-emerald-800 hover:bg-emerald-900" data-testid="confirm-review-button">
              {savingReview ? <><Loader2 className="ml-2 h-4 w-4 animate-spin" /> جارٍ الحفظ...</> : `اعتماد واستيراد الطلاب (${reviewRows.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete all confirmation */}
      <AlertDialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <AlertDialogContent className="text-right">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف جميع الطلاب</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف جميع الطلاب؟ لا يمكن التراجع عن هذه العملية. (سيتم حذف طلاب هذه المدرسة فقط دون التأثير على أي مدرسة أخرى.)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-all">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); deleteAll(); }} disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-all">
              {deletingAll ? "جارٍ الحذف..." : "نعم، احذف الجميع"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick send dialog */}
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="text-right">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-700" /> إرسال سريع
            </DialogTitle>
          </DialogHeader>
          {quickStudent && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="font-bold text-slate-800">{quickStudent.name}</p>
                <p className="text-xs text-slate-400">{quickStudent.grade} — {quickStudent.class_name}</p>
                <p dir="ltr" className="text-right font-mono text-xs text-emerald-700">{quickStudent.parent_phone}</p>
              </div>
              <div>
                <Label className="mb-1 block text-xs text-slate-500">القالب</Label>
                <Select value={quickTemplate} onValueChange={setQuickTemplate}>
                  <SelectTrigger data-testid="quick-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {quickTemplate === "custom" && (
                <div>
                  <Label className="mb-1 block text-xs text-slate-500">نص الرسالة</Label>
                  <Textarea value={quickCustom} onChange={(e) => setQuickCustom(e.target.value)} rows={3}
                    data-testid="quick-custom-message" placeholder="يمكنك استخدام: {student_name} {school_name} {grade} {class} {date}" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={doQuickSend} disabled={quickSending} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="quick-send-confirm">
              {quickSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> إرسال</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="text-right">
          <DialogHeader><DialogTitle>{editId ? "تعديل بيانات الطالب" : "إضافة طالب جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="اسم الطالب *" v={form.name} on={(v) => setForm({ ...form, name: v })} tid="student-name" />
            <Field label="رقم الهوية" v={form.national_id} on={(v) => setForm({ ...form, national_id: v })} tid="student-national-id" />
            <Field label="الصف *" v={form.grade} on={(v) => setForm({ ...form, grade: v })} tid="student-grade" />
            <Field label="الفصل *" v={form.class_name} on={(v) => setForm({ ...form, class_name: v })} tid="student-class" />
            <Field label="رقم الجوال" v={form.parent_phone} on={(v) => setForm({ ...form, parent_phone: v })} tid="student-parent-phone" ltr />
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="save-student-button">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, tid, ltr }) => (
  <div>
    <Label className="mb-1 block text-xs text-slate-500">{label}</Label>
    <Input value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid}
      dir={ltr ? "ltr" : "rtl"} className={ltr ? "text-right font-mono" : ""} />
  </div>
);
